// src/services/costs/import.js
// Importer für Standard-BKP-Daten

import { defaultBkpData } from "./defaultBkpData";
import { calculateTotal } from "./calculations.js";

export function importDefaultBkpStructure() {
  const tree = {};

  for (const row of defaultBkpData) {
    const level = row.code.length;
    const parentCode = level > 1 ? row.code.slice(0, level - 1) : undefined;

    const node = {
      code: row.code,
      name: row.name,
      unit: row.unit,
      unitPrice: row.unitPrice,
      totalComputed: 0,
      active: false,
      level,
      parentCode,
      children: [],
    };

    node.totalComputed = calculateTotal(node);
    tree[node.code] = node;
  }

  // Kinder-Referenzen aufbauen
  for (const code in tree) {
    const node = tree[code];
    if (node.parentCode && tree[node.parentCode]) {
      const parent = tree[node.parentCode];
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(code);
    }
  }

  return tree;
}

export function getNextChildCode(parentCode, tree) {
  const dotCount = (parentCode.match(/\./g) || []).length;

  // Level 4 (z.B. 211.5) -> erstelle Level 5 (211.51, 211.52, ..., 211.59, 211.510)
  if (dotCount === 1) {
    const afterDot = parentCode.split(".")[1];

    // Prüfe ob Parent Level 4 ist (eine Ziffer nach Punkt)
    if (afterDot.length === 1) {
      const children = Object.keys(tree).filter((code) => {
        if (!code.startsWith(parentCode)) return false;
        const parts = code.split(".");
        if (parts.length !== 2) return false;
        // Level 5 hat mindestens 2 Ziffern nach dem Punkt
        return parts[1].length >= 2 && code !== parentCode;
      });

      if (children.length === 0) {
        return `${parentCode}1`;
      }

      const numbers = children
        .map((c) => {
          const parts = c.split(".");
          // Nimm alles nach dem ersten Zeichen nach dem Punkt (z.B. "211.59" -> "9")
          const numberPart = parts[1].substring(1);
          return parseInt(numberPart, 10);
        })
        .filter((n) => !isNaN(n));
      const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;

      return `${parentCode}${maxNum + 1}`;
    }
  }

  // Level 3 (z.B. 211) oder Overflow-Codes ohne Punkt (z.B. 2010, wenn 200-209 belegt sind)
  // -> erstelle dotted children (211.1, 211.2, ... / 2010.1, 2010.2, ...)
  if (parentCode.length >= 3 && !parentCode.includes(".")) {
    const children = Object.keys(tree).filter((code) => {
      if (!code.startsWith(parentCode + ".")) return false;
      const parts = code.split(".");
      if (parts.length !== 2) return false;
      // Ein-Ziffern-Kinder (level 4 im Standardfall)
      return parts[1].length === 1;
    });

    if (children.length === 0) {
      return `${parentCode}.1`;
    }

    const numbers = children
      .map((c) => {
        const afterDot = c.split(".")[1];
        return parseInt(afterDot, 10);
      })
      .filter((n) => !isNaN(n));
    const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;

    return `${parentCode}.${maxNum + 1}`;
  }

  // Level 1-2: alte Logik
  const children = Object.keys(tree).filter(
    (code) =>
      code.startsWith(parentCode) &&
      code.length === parentCode.length + 1 &&
      !code.includes(".")
  );

  if (children.length === 0) {
    return `${parentCode}1`;
  }

  const numbers = children
    .map((c) => parseInt(c.slice(-1), 10))
    .filter((n) => !isNaN(n));
  const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;

  return `${parentCode}${maxNum + 1}`;
}

// Neue Funktion hinzufügen
export function importBkpFromCsv(csvText) {
  const lines = csvText.split("\n").slice(1); // Header überspringen
  const tree = {};

  // Hilfsfunktion für Level-Berechnung
  // Hilfsfunktion für Level-Berechnung
  const getLevel = (code) => {
    if (code.includes(".")) {
      const afterDot = code.split(".")[1];
      if (afterDot.length === 1) {
        return 4; // z.B. 211.5 (eine Ziffer nach Punkt)
      } else if (afterDot.length === 2) {
        return 5; // z.B. 211.51 (zwei Ziffern nach Punkt)
      }
    }
    return code.length; // 1, 2 oder 3
  };

  // Hilfsfunktion für Parent-Code
  // Hilfsfunktion für Parent-Code
  const getParentCode = (code) => {
    if (code.includes(".")) {
      const parts = code.split(".");
      const afterDot = parts[1];

      if (afterDot.length === 2) {
        // Level 5: z.B. 211.51 -> Parent ist 211.5
        return `${parts[0]}.${afterDot.charAt(0)}`;
      } else if (afterDot.length === 1) {
        // Level 4: z.B. 211.5 -> Parent ist 211
        return parts[0];
      }
    }
    if (code.length > 1) {
      // 141 -> Parent ist 14
      return code.substring(0, code.length - 1);
    }
    return null; // Level 1 hat keinen Parent
  };

  for (const line of lines) {
    if (!line.trim()) continue;

    const [code, name, quantity, unit] = line
      .split(";")
      .map((s) => s?.trim() || "");
    if (!code) continue;

    const level = getLevel(code);
    const parentCode = getParentCode(code);

    const node = {
      code,
      name: name || "",
      source: "",
      length: 0,
      width: 0,
      height: 0,
      quantity: parseInt(quantity, 10) || 1,
      unit: unit === "p" ? "pl" : unit || "pl",
      unitPrice: 0,
      totalComputed: 0,
      active: false,
      level,
      parentCode,
      children: [],
      tags: [],
    };

    tree[code] = node;

    // Parent-Children Beziehung aufbauen
    if (parentCode && tree[parentCode]) {
      if (!tree[parentCode].children.includes(code)) {
        tree[parentCode].children.push(code);
      }
    }
  }

  // Zweiter Durchlauf für fehlende Parent-Children Beziehungen
  Object.values(tree).forEach((node) => {
    if (node.parentCode && tree[node.parentCode]) {
      if (!tree[node.parentCode].children.includes(node.code)) {
        tree[node.parentCode].children.push(node.code);
      }
    }
  });

  return tree;
}
